/**
 * @param {string} path
 */
export function stripPageFromPath(path) {
  return path.replace(/\/page\/[0-9]+\/$/, '/')
}

/**
 * @param {string} path
 * @returns {number}
 */
export function readPageFromPath(path = location.pathname) {
  const match = path.match(/\/page\/([0-9]+)\/$/)
  return match ? Number(match[1]) : 1
}

/**
 * @param {URL} url
 * @returns {string}
 */
export function getRequestUriFromUrl(url) {
  return url.toString().slice(url.origin.length).split('#').shift()
}

/**
 * @param {string|undefined} urlString
 */
export function getQueryParamsObject(urlString = location.href) {
  return parseQuery(new URL(urlString).search.replace(/^\?/, ''))
}

/**
 * @param {string} query
 * @returns {Record<string, any>}
 */
export function parseQuery(query) {
  /**
   * @type {Record<string, any>}
   */
  const array = {}

  const rawParams = String(query).replace(/^&/, '').replace(/&$/, '').split('&')

  /**
   * @param {string} string
   * @returns {string}
   */
  function fixEncodedString(string) {
    return decodeURIComponent(string.replace(/\+/g, '%20'))
  }

  for (const rawParam of rawParams) {
    let [key, value = ''] = rawParam.split('=').map(fixEncodedString)

    if (key && key.charAt(0) !== '[') {
      let keys = []
      let postLeftBracketPos = 0

      for (let i = 0; i < key.length; i++) {
        if (key.charAt(i) === '[' && !postLeftBracketPos) {
          postLeftBracketPos = i + 1
        } else if (key.charAt(i) === ']') {
          if (postLeftBracketPos) {
            if (!keys.length) {
              keys.push(key.slice(0, postLeftBracketPos - 1))
            }

            keys.push(key.substr(postLeftBracketPos, i - postLeftBracketPos))
            postLeftBracketPos = 0

            if (key.charAt(i + 1) !== '[') {
              break
            }
          }
        }
      }

      if (!keys.length) {
        keys = [key]
      }

      for (let i = 0; i < keys[0].length; i++) {
        const chr = keys[0].charAt(i)

        if (chr === ' ' || chr === '.' || chr === '[') {
          keys[0] = keys[0].slice(0, i) + '_' + keys[0].slice(i + 1)
        }

        if (chr === '[') {
          break
        }
      }

      let object = array

      /**
       * @type {Record<string, any>}
       */
      let lastObject

      for (let i = 0; i < keys.length; i++) {
        key = keys[i].replace(/^['"]/, '').replace(/['"]$/, '')
        lastObject = object

        if ((key === '' || key === ' ') && i !== 0) {
          // Insert new dimension
          let ct = -1

          for (const p of Object.keys(object)) {
            if (Number(p) > ct && p.match(/^\d+$/g)) {
              ct = Number(p)
            }
          }

          key = String(ct + 1)
        }

        // if primitive value, replace with object
        if (typeof object[key] !== 'object') {
          object[key] = {}
        }

        object = object[key]
      }

      lastObject[key] = value
    }
  }

  return array
}

/**
 * Parse a flat FormData object into a PHP-like object
 * (e.g. properly handling fields with names like "name[]")
 *
 * @param {FormData} formData
 * @returns {Record<string, any>}
 */
export function parseFormData(formData) {
  // Strategy: Convert FormData to URLSearchParams, then parse the query string
  const params = new URLSearchParams(formData)
  return parseQuery(params.toString())
}

/**
 * Build a query string from an object with support for nested arrays and objects
 *
 * @param {Record<string, any>} data
 * @returns {string}
 */
export function buildQuery(data) {
  /**
   * @param {string} value
   * @returns {string}
   */
  function encode(value) {
    return encodeURIComponent(value).replaceAll('%5B', '[').replaceAll('%5D', ']')
  }

  /**
   * @param string key
   * @param {any} value
   * @returns {string}
   */
  function assembleQuery(key, value) {
    if (value == null) return ''

    if (Array.isArray(value)) {
      return value
        .filter(valueItem => valueItem != null)
        .map((valueItem, index) => assembleQuery(`${key}[${index}]`, valueItem))
        .join('&')
    }

    if (typeof value === 'object') {
      return Object.entries(value)
        .flatMap(([propertyKey, propertyValue]) =>
          propertyValue == null ? [] : assembleQuery(`${key}[${propertyKey}]`, propertyValue),
        )
        .join('&')
    }

    if (typeof value !== 'function') {
      return `${encode(key)}=${encode(value)}`
    }

    throw new Error('There was an error processing for http_build_query().')
  }

  return Object.entries(data)
    .flatMap(([key, value]) => assembleQuery(key, value) || [])
    .join('&')
}

/**
 * Build an URL
 *
 * @param {{
 *   page?: number,
 *   queryParams?: Record<string, any>,
 *   base?: string
 * }|undefined} options
 * @returns
 */
export function generateUrl({ page, queryParams, base } = {}) {
  let url
  if (base == undefined) {
    url = new URL(window.location.href)

    if (queryParams != undefined) {
      url.search = ''
    }
  } else {
    url = new URL(base)
  }
  page ??= readPageFromPath(url.pathname)

  url.pathname = stripPageFromPath(url.pathname)
  if (page > 1) {
    url.pathname = `${url.pathname.replace(/\/$/, '')}/page/${page}/`
  }

  if (queryParams != undefined) {
    url.search = buildQuery(queryParams)
  }

  return url.toString()
}
